<template>
  <b-card class="kyc-info-card">
    <div class="section-head">
      <h5>KYC Verification</h5>
      <span class="status-pill" :class="statusClass">{{ statusLabel }}</span>
    </div>

    <div v-if="isLoading" class="text-center py-4"><b-spinner></b-spinner></div>

    <template v-else>
      <!-- LIMITS : always visible, they explain WHY KYC matters -->
      <div class="tier-line">
        Your tier: <strong class="capitalize">{{ tier }}</strong>
      </div>

      <div class="limit-group">
        <h6>Transaction Limits</h6>
        <div class="limit-row" v-for="(l, i) in txnLimitRows" :key="'t' + i">
          <span>{{ l.label }}</span><strong>{{ l.value }}</strong>
        </div>
      </div>

      <div class="limit-group">
        <h6>Withdrawal Limits</h6>
        <div class="limit-row" v-for="(l, i) in wdLimitRows" :key="'w' + i">
          <span>{{ l.label }}</span><strong>{{ l.value }}</strong>
        </div>
      </div>

      <!-- NOT SUBMITTED -->
      <div v-if="!submitted" class="notice mt-3">
        Your account is not verified yet. Complete your KYC to raise these limits.
      </div>

      <!-- SUBMITTED, UNDER REVIEW -->
      <div v-else-if="!isVerified" class="notice pending mt-3">
        Your KYC has been submitted and is under review.
        <template v-if="canEdit"> You can still update it until it is approved.</template>
      </div>

      <!-- VERIFIED -->
      <div v-else class="notice done mt-3">
        Your KYC is verified.
        <template v-if="!canEdit"> These details are locked.</template>
      </div>

      <div v-if="pendingUpdateRequest" class="notice pending mt-2">
        An update request is already pending with the Admin. The new details appear here once
        it is approved.
      </div>

      <div class="mt-3">
        <b-button
          variant="primary"
          v-if="showFormButton"
          :disabled="pendingUpdateRequest"
          @click="openForm"
        >{{ formButtonLabel }}</b-button>
        <b-button variant="primary" v-if="submitted" class="ml-2" @click="openDetail">
          View Details
        </b-button>
      </div>
    </template>

    <!-- KYC FORM -->
    <b-modal id="user-kyc-modal" centered size="lg" :title="formTitle">
      <div class="kyc-form">
      <h6 class="grp">Personal</h6>
      <div class="row">
        <div class="col-6 form-row-x">
          <label>Gender *</label>
          <b-form-select v-model="form.gender" :options="genderOptions"></b-form-select>
          <span class="text-danger" v-if="errors.gender">{{ errors.gender }}</span>
        </div>
        <div class="col-6 form-row-x">
          <label>Marital Status *</label>
          <b-form-select v-model="form.marital_status" :options="maritalOptions"></b-form-select>
          <span class="text-danger" v-if="errors.marital_status">{{ errors.marital_status }}</span>
        </div>
      </div>
      <div class="row">
        <div class="col-6 form-row-x">
          <label>Occupation *</label>
          <b-form-input v-model.trim="form.occupation"></b-form-input>
          <span class="text-danger" v-if="errors.occupation">{{ errors.occupation }}</span>
        </div>
        <div class="col-6 form-row-x">
          <label>Date of Birth *</label>
          <b-form-input v-model="form.date_of_birth" type="date" :max="maxDob"></b-form-input>
          <span class="text-danger" v-if="errors.date_of_birth">{{ errors.date_of_birth }}</span>
        </div>
      </div>
      <div class="form-row-x">
        <b-form-checkbox v-model="form.no_criminal_records">
          I declare that I have no criminal records.
        </b-form-checkbox>
        <span class="text-danger" v-if="errors.no_criminal_records">{{ errors.no_criminal_records }}</span>
      </div>

      <h6 class="grp">Permanent Address</h6>
      <div class="row">
        <div class="col-6 form-row-x">
          <label>Country *</label>
          <SearchSelect
            v-model="form.permanent_country"
            :options="countryOptions"
            value-key="name"
            label-key="name"
            placeholder="Select country"
            clearable
            :has-error="!!errors.permanent_country"
          ></SearchSelect>
          <span class="text-danger" v-if="errors.permanent_country">{{ errors.permanent_country }}</span>
        </div>
        <div class="col-6 form-row-x">
          <label>Province / State *</label>
          <b-form-input v-model.trim="form.permanent_province"></b-form-input>
          <span class="text-danger" v-if="errors.permanent_province">{{ errors.permanent_province }}</span>
        </div>
      </div>
      <!-- City and Postal Code were col-4 + col-4, left over from when Ward held the
           third slot. With Ward gone they sat two-thirds across with a gap on the right. -->
      <div class="row">
        <div class="col-12 col-md-6 form-row-x">
          <label>City *</label>
          <b-form-input v-model.trim="form.permanent_city"></b-form-input>
          <span class="text-danger" v-if="errors.permanent_city">{{ errors.permanent_city }}</span>
        </div>
        <div class="col-12 col-md-6 form-row-x">
          <label>Postal Code</label>
          <b-form-input v-model.trim="form.permanent_postal_code"></b-form-input>
        </div>
      </div>
      <div class="form-row-x">
        <label>Street</label>
        <b-form-input v-model.trim="form.permanent_street"></b-form-input>
      </div>

      <h6 class="grp">Temporary Address</h6>
      <div class="form-row-x">
        <b-form-checkbox v-model="form.temporary_same_as_permanent">
          Same as permanent address
        </b-form-checkbox>
      </div>
      <template v-if="!form.temporary_same_as_permanent">
        <div class="row">
          <div class="col-6 form-row-x">
            <label>Country</label>
            <SearchSelect
              v-model="form.temporary_country"
              :options="countryOptions"
              value-key="name"
              label-key="name"
              placeholder="Select country"
              clearable
            ></SearchSelect>
          </div>
          <div class="col-6 form-row-x">
            <label>Province / State</label>
            <b-form-input v-model.trim="form.temporary_province"></b-form-input>
          </div>
        </div>
        <div class="row">
          <div class="col-12 col-md-6 form-row-x">
            <label>City</label>
            <b-form-input v-model.trim="form.temporary_city"></b-form-input>
          </div>
          <div class="col-12 col-md-6 form-row-x">
            <label>Postal Code</label>
            <b-form-input v-model.trim="form.temporary_postal_code"></b-form-input>
          </div>
        </div>
        <div class="form-row-x">
          <label>Street</label>
          <b-form-input v-model.trim="form.temporary_street"></b-form-input>
        </div>
      </template>

      <h6 class="grp">Identity Document</h6>
      <div class="row">
        <div class="col-6 form-row-x">
          <label>Document Type *</label>
          <b-form-select v-model="form.document_type" :options="documentTypeOptions"></b-form-select>
          <span class="text-danger" v-if="errors.document_type">{{ errors.document_type }}</span>
        </div>
        <div class="col-6 form-row-x">
          <label>Document Number *</label>
          <b-form-input v-model.trim="form.document_number"></b-form-input>
          <span class="text-danger" v-if="errors.document_number">{{ errors.document_number }}</span>
        </div>
      </div>
      <div class="row">
        <div class="col-4 form-row-x">
          <label>Selfie *</label>
          <input type="file" class="form-control" ref="selfie"
            accept="image/png, image/jpeg, image/jpg" @change="onFile($event, 'selfie')" />
          <small class="muted" v-if="files.selfie">{{ files.selfie.name }}</small>
          <span class="text-danger" v-if="errors.selfie">{{ errors.selfie }}</span>
        </div>
        <div class="col-4 form-row-x">
          <label>Front Image *</label>
          <input type="file" class="form-control" ref="front_image"
            accept="image/png, image/jpeg, image/jpg" @change="onFile($event, 'front_image')" />
          <small class="muted" v-if="files.front_image">{{ files.front_image.name }}</small>
          <span class="text-danger" v-if="errors.front_image">{{ errors.front_image }}</span>
        </div>
        <div class="col-4 form-row-x">
          <label>Back Image{{ backRequired ? ' *' : '' }}</label>
          <input type="file" class="form-control" ref="back_image"
            accept="image/png, image/jpeg, image/jpg" @change="onFile($event, 'back_image')" />
          <small class="muted" v-if="files.back_image">{{ files.back_image.name }}</small>
          <span class="text-danger" v-if="errors.back_image">{{ errors.back_image }}</span>
        </div>
      </div>
      <small class="muted">Accepted: JPG or PNG, max 5 MB each.</small>
      <div class="text-danger mt-2" v-if="formError">{{ formError }}</div>
      </div>

      <template #modal-footer="{ cancel }">
        <b-button size="sm" variant="success" :disabled="isSaving" @click="submit">
          <b-spinner small v-if="isSaving" class="mr-1"></b-spinner>
          {{ isSaving ? "Submitting..." : formSubmitLabel }}
        </b-button>
        <b-button size="sm" variant="secondary" @click="cancel()">Cancel</b-button>
      </template>
    </b-modal>

    <!-- DETAIL -->
    <b-modal id="user-kyc-detail" centered size="lg" title="KYC Details" hide-footer body-class="kyc-detail-body">
      <!-- summary header -->
      <div class="kd-head">
        <div class="kd-avatar">
          <img v-if="selfieUrl" :src="selfieUrl" alt="Selfie" @click="openImage({ label: 'Selfie', url: selfieUrl })" />
          <span v-else class="material-icons">person</span>
        </div>
        <div class="kd-head-body">
          <div class="kd-name">{{ holderName }}</div>
          <div class="kd-sub">{{ documentTypeLabel(kycData.document_type) || 'Identity document' }}</div>
          <div class="kd-badges">
            <span class="status-pill" :class="statusClass">{{ statusLabel }}</span>
            <span class="tier-pill capitalize">{{ tier }} tier</span>
          </div>
        </div>
      </div>

      <!-- grouped facts, two columns on wide screens -->
      <div class="kd-sec" v-for="(sec, si) in detailSections" :key="'sec' + si">
        <h6 class="kd-grp">{{ sec.title }}</h6>
        <div class="kd-grid">
          <div class="kd-item" v-for="(f, i) in sec.items" :key="'d' + si + '-' + i">
            <span class="kd-label">{{ f.label }}</span>
            <span class="kd-value">{{ f.value || '-' }}</span>
          </div>
        </div>
      </div>

      <!-- documents -->
      <div class="kd-sec" v-if="documentImages.length">
        <h6 class="kd-grp">Documents</h6>
        <div class="kd-docs">
          <div class="kd-doc" v-for="(img, i) in documentImages" :key="'img' + i" @click="openImage(img)">
            <img :src="img.url" :alt="img.label" />
            <div class="kd-doc-foot">
              <span>{{ img.label }}</span>
              <span class="material-icons">zoom_in</span>
            </div>
          </div>
        </div>
      </div>

      <p class="kd-note" v-if="!canEdit">
        Verified details cannot be changed directly. Submit an update request and the changes
        appear here once an admin approves them.
      </p>
    </b-modal>

    <b-modal id="kyc-image-preview" centered size="lg" :title="previewImage.label" hide-footer>
      <div class="text-center">
        <img v-if="previewImage.url" :src="previewImage.url" class="preview-img" />
      </div>
    </b-modal>

    <b-toast id="kyc-toast" variant="success" solid append-toast toaster="b-toaster-top-center">
      <div slot="toast-title" class="d-flex flex-grow-1 align-items-baseline">
        <strong class="mr-auto">Amej Pay</strong>
      </div>
      <div><p>{{ infoText }}</p></div>
    </b-toast>
  </b-card>
</template>

<script>
import { mapActions, mapState } from "vuex";
import { formatMoney } from "@/utils/format";
import SearchSelect from "../reuseable_components/SearchSelect.vue";

const MAX_FILE = 5 * 1024 * 1024;

/**
 * Ward was dropped from the form. The backend still declares the field, so a fixed
 * value goes up with every submission; nothing on screen references it.
 */
const DEFAULT_WARD = "1";

export default {
  name: "KycInfo",
  components: { SearchSelect: SearchSelect },
  data() {
    return {
      isLoading: false,
      isSaving: false,
      infoText: "",
      formError: "",
      errors: {},
      form: {
        gender: "", marital_status: "", occupation: "", no_criminal_records: false,
        permanent_country: "", permanent_province: "", permanent_city: "",
        permanent_ward: "", permanent_postal_code: "", permanent_street: "",
        temporary_same_as_permanent: true,
        temporary_country: "", temporary_province: "", temporary_city: "",
        temporary_ward: "", temporary_postal_code: "", temporary_street: "",
        date_of_birth: "", document_type: "", document_number: "",
      },
      files: { selfie: null, front_image: null, back_image: null },
      previewImage: { label: "", url: "" },
      genderOptions: [
        { value: "", text: "Select" },
        { value: "male", text: "Male" },
        { value: "female", text: "Female" },
        { value: "other", text: "Other" },
      ],
      maritalOptions: [
        { value: "", text: "Select" },
        { value: "single", text: "Single" },
        { value: "married", text: "Married" },
        { value: "divorced", text: "Divorced" },
        { value: "widowed", text: "Widowed" },
      ],
    };
  },
  computed: {
    ...mapState(["userKyc", "documentTypes", "profile", "countries"]),

    /** Country names for the searchable select; the KYC record stores the NAME. */
    countryOptions: function () {
      const list = Array.isArray(this.countries) ? this.countries : [];
      return list
        .map(function (c) {
          const name = (typeof c === "string") ? c : (c && (c.name || c.country_name || c.title || c.country));
          return name ? { name: name } : null;
        })
        .filter(function (o) { return !!o; });
    },
    kyc: function () { return this.userKyc || {}; },
    submitted: function () { return this.kyc.submitted === true; },
    isVerified: function () {
      return this.kyc.is_verified === true || this.kyc.is_kyc_verified === true;
    },
    canEdit: function () { return this.kyc.can_edit === true; },
    canRequestUpdate: function () { return this.kyc.can_request_update === true; },
    pendingUpdateRequest: function () { return !!this.kyc.pending_update_request; },
    /**
     * Once the record is locked (can_edit false) the same form is submitted as an
     * UPDATE REQUEST to /kyc/update-requests instead of overwriting the KYC.
     */
    isUpdateRequest: function () {
      return this.submitted && !this.canEdit;
    },
    /** Always reachable: direct edit while allowed, otherwise an update request. */
    showFormButton: function () {
      if (!this.submitted) { return true; }
      if (this.canEdit) { return true; }
      return this.canRequestUpdate;
    },
    formButtonLabel: function () {
      if (!this.submitted) { return "Submit KYC"; }
      if (this.canEdit) { return "Update KYC"; }
      return this.pendingUpdateRequest ? "Update Requested" : "Request KYC Update";
    },
    formTitle: function () {
      if (!this.submitted) { return "Submit KYC"; }
      return this.isUpdateRequest ? "Request KYC Update" : "Update KYC";
    },
    formSubmitLabel: function () {
      return this.isUpdateRequest ? "Send Update Request" : "Submit KYC";
    },
    statusLabel: function () {
      const s = String(this.kyc.status || "not_submitted");
      return s.split("_").map(function (w) {
        return w.charAt(0).toUpperCase() + w.slice(1);
      }).join(" ");
    },
    statusClass: function () {
      if (this.isVerified) { return "is-success"; }
      if (this.submitted) { return "is-pending"; }
      return "is-muted";
    },
    tier: function () {
      const l = this.kyc.limits || {};
      return l.tier || (l.limits && l.limits.tier) || "unverified";
    },
    txnLimitRows: function () { return this.limitRows(this.kyc.limits); },
    wdLimitRows: function () { return this.limitRows(this.kyc.withdraw_limits); },
    documentTypeOptions: function () {
      const list = Array.isArray(this.documentTypes) ? this.documentTypes : [];
      return [{ value: "", text: "Select document type" }].concat(
        list.slice().sort(function (a, b) { return (a.order || 0) - (b.order || 0); })
          // send the CODE (e.g. "citizenship"), not the numeric id
          .map(function (d) { return { value: d.code, text: d.name }; })
      );
    },
    /** requires_back on the chosen document type drives the back image. */
    backRequired: function () {
      const list = Array.isArray(this.documentTypes) ? this.documentTypes : [];
      const found = list.filter(
        function (d) { return String(d.code) === String(this.form.document_type); }.bind(this)
      );
      return found.length ? !!found[0].requires_back : false;
    },
    maxDob: function () {
      const d = new Date();
      d.setDate(d.getDate() - 1);
      const m = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      return d.getFullYear() + "-" + m + "-" + day;
    },
    /** The submitted record, wherever the API nests it. */
    kycData: function () {
      const k = this.kyc;
      return k.data || k.kyc || k.detail || k;
    },
    /** Every submitted field, grouped. Blank groups are dropped. */
    detailSections: function () {
      const k = this.kyc;
      const d = this.kycData;
      const yn = function (v) { return v ? "Yes" : "No"; };
      const docLabel = this.documentTypeLabel(d.document_type);
      const sections = [
        {
          title: "Status",
          items: [
            { label: "Status", value: this.statusLabel },
            { label: "Tier", value: this.tier },
            { label: "Verified", value: yn(this.isVerified) },
            { label: "Editable", value: yn(this.canEdit) },
            { label: "Pending Update Request", value: yn(this.pendingUpdateRequest) },
          ],
        },
        {
          title: "Personal",
          items: [
            { label: "Gender", value: this.prettyWord(d.gender) },
            { label: "Marital Status", value: this.prettyWord(d.marital_status) },
            { label: "Occupation", value: d.occupation },
            { label: "Date of Birth", value: d.date_of_birth || k.date_of_birth },
            { label: "No Criminal Records", value: d.no_criminal_records === undefined ? "" : yn(d.no_criminal_records) },
          ],
        },
        {
          title: "Permanent Address",
          items: [
            { label: "Country", value: d.permanent_country },
            { label: "Province / State", value: d.permanent_province },
            { label: "City", value: d.permanent_city },
            { label: "Postal Code", value: d.permanent_postal_code },
            { label: "Street", value: d.permanent_street },
          ],
        },
        {
          title: "Temporary Address",
          items: [
            { label: "Same as Permanent", value: d.temporary_same_as_permanent === undefined ? "" : yn(d.temporary_same_as_permanent) },
            { label: "Country", value: d.temporary_country },
            { label: "Province / State", value: d.temporary_province },
            { label: "City", value: d.temporary_city },
            { label: "Postal Code", value: d.temporary_postal_code },
            { label: "Street", value: d.temporary_street },
          ],
        },
        {
          title: "Identity Document",
          items: [
            { label: "Document Type", value: docLabel },
            { label: "Document Number", value: d.document_number },
          ],
        },
      ];
      // Keep a group only if it actually has data (Status always shows).
      return sections.filter(function (s, idx) {
        if (idx === 0) { return true; }
        return s.items.some(function (i) { return i.value !== "" && i.value !== null && i.value !== undefined; });
      });
    },
    holderName: function () {
      const a = this.profile && this.profile.author;
      return (a && a.full_name) || "Account holder";
    },
    selfieUrl: function () {
      const d = this.kycData;
      return this.resolveMedia(d.selfie || d.selfie_url);
    },
    documentImages: function () {
      const d = this.kycData;
      const vm = this;
      const map = [
        { key: "selfie", label: "Selfie" },
        { key: "front_image", label: "Front Image" },
        { key: "back_image", label: "Back Image" },
      ];
      const out = [];
      map.forEach(function (m) {
        const url = vm.resolveMedia(d[m.key] || d[m.key + "_url"]);
        if (url) { out.push({ label: m.label, url: url }); }
      });
      return out;
    },
  },
  created() {
    this.load();
  },
  watch: {
    // When the KYC record arrives (or changes), keep the edit form in sync.
    userKyc: {
      handler: function () { this.prefillForm(); },
      deep: true,
    },
  },
  methods: {
    ...mapActions(["getUserKyc", "submitUserKyc", "requestUserKycUpdate", "listDocumentTypes", "listCountries"]),
    load() {
      this.isLoading = true;
      const vm = this;
      return Promise.all([this.getUserKyc(), this.listDocumentTypes(), this.listCountries()])
        .then(function () { vm.isLoading = false; })
        .catch(function () { vm.isLoading = false; });
    },
    /** Renders a limits block as label/value rows; null means "no limit". */
    limitRows(block) {
      const b = block || {};
      const l = b.limits || {};
      const u = b.usage || {};
      const amt = function (v) { return v === null || v === undefined ? "No limit" : "$ " + formatMoney(v); };
      const cnt = function (v) { return v === null || v === undefined ? "No limit" : String(v); };
      return [
        { label: "Per transaction", value: amt(l.min_amount) + " - " + amt(l.max_amount) },
        { label: "Daily amount", value: "$ " + formatMoney(u.daily_amount) + " / " + amt(l.daily_amount) },
        { label: "Monthly amount", value: "$ " + formatMoney(u.monthly_amount) + " / " + amt(l.monthly_amount) },
        { label: "Daily count", value: (u.daily_count || 0) + " / " + cnt(l.daily_count) },
        { label: "Monthly count", value: (u.monthly_count || 0) + " / " + cnt(l.monthly_count) },
      ];
    },
    prettyWord(v) {
      if (!v) { return ""; }
      return String(v).split("_").map(function (w) {
        return w.charAt(0).toUpperCase() + w.slice(1);
      }).join(" ");
    },
    documentTypeLabel(value) {
      if (!value) { return ""; }
      if (value && typeof value === "object") { return value.name || value.code || ""; }
      const list = Array.isArray(this.documentTypes) ? this.documentTypes : [];
      const found = list.filter(function (d) {
        return String(d.code) === String(value) || String(d.id) === String(value);
      });
      return found.length ? found[0].name : this.prettyWord(value);
    },
    /** Document type can come back as a code, an id or a nested object. */
    documentTypeCode(value) {
      if (!value) { return ""; }
      if (value && typeof value === "object") { return value.code || ""; }
      const list = Array.isArray(this.documentTypes) ? this.documentTypes : [];
      const found = list.filter(function (d) {
        return String(d.code) === String(value) || String(d.id) === String(value);
      });
      return found.length ? found[0].code : String(value);
    },
    resolveMedia(path) {
      if (!path) { return ""; }
      if (typeof path === "object") { path = path.url || path.file || path.image || ""; }
      if (!path) { return ""; }
      if (/^https?:\/\//i.test(path) || /^data:/i.test(path)) { return path; }
      const media = process.env.VUE_APP_MEDIA_URL || "";
      if (media) { return media.replace(/\/$/, "") + "/" + String(path).replace(/^\//, ""); }
      const base = process.env.VUE_APP_SERVICE_URL || "";
      let origin = base;
      try { origin = new URL(base).origin; }
      catch (e) { origin = base.replace(/\/v1\/?$/, ""); }
      return origin.replace(/\/$/, "") + "/" + String(path).replace(/^\//, "");
    },
    openImage(img) {
      this.previewImage = img;
      this.$bvModal.show("kyc-image-preview");
    },
    /** Load whatever the API already holds into the form so an update is a real edit. */
    prefillForm() {
      const d = this.kycData;
      const f = this.form;
      const set = function (key, value) {
        if (value !== undefined && value !== null && value !== "") { f[key] = value; }
      };
      set("gender", d.gender);
      set("marital_status", d.marital_status);
      set("occupation", d.occupation);
      if (d.no_criminal_records !== undefined) { f.no_criminal_records = !!d.no_criminal_records; }
      set("permanent_country", d.permanent_country);
      set("permanent_province", d.permanent_province);
      set("permanent_city", d.permanent_city);
      set("permanent_ward", d.permanent_ward);
      set("permanent_postal_code", d.permanent_postal_code);
      set("permanent_street", d.permanent_street);
      if (d.temporary_same_as_permanent !== undefined) {
        f.temporary_same_as_permanent = !!d.temporary_same_as_permanent;
      }
      set("temporary_country", d.temporary_country);
      set("temporary_province", d.temporary_province);
      set("temporary_city", d.temporary_city);
      set("temporary_ward", d.temporary_ward);
      set("temporary_postal_code", d.temporary_postal_code);
      set("temporary_street", d.temporary_street);
      set("document_number", d.document_number);
      const code = this.documentTypeCode(d.document_type);
      if (code) { f.document_type = code; }
      f.date_of_birth = d.date_of_birth || this.kyc.date_of_birth ||
        ((this.profile && this.profile.author && this.profile.author.date_of_birth) || f.date_of_birth || "");
    },
    openForm() {
      this.errors = {};
      this.formError = "";
      this.prefillForm();
      this.$bvModal.show("user-kyc-modal");
    },
    openDetail() { this.$bvModal.show("user-kyc-detail"); },
    onFile(e, field) {
      const file = e.target.files && e.target.files[0];
      if (!file) { return; }
      const allowed = ["image/png", "image/jpeg", "image/jpg"];
      if (allowed.indexOf(file.type) === -1) {
        this.files[field] = null; e.target.value = "";
        this.$set(this.errors, field, "Please upload a JPG or PNG image.");
        return;
      }
      if (file.size > MAX_FILE) {
        this.files[field] = null; e.target.value = "";
        this.$set(this.errors, field, "File is too large (max 5 MB).");
        return;
      }
      this.files[field] = file;
      this.$set(this.errors, field, "");
    },
    validate() {
      const f = this.form;
      const e = {};
      if (!f.gender) { e.gender = "Gender is required."; }
      if (!f.marital_status) { e.marital_status = "Marital status is required."; }
      if (!f.occupation) { e.occupation = "Occupation is required."; }
      if (!f.no_criminal_records) { e.no_criminal_records = "You must confirm this declaration."; }
      if (!f.date_of_birth) { e.date_of_birth = "Date of birth is required."; }
      else if (f.date_of_birth >= this.todayIso()) { e.date_of_birth = "Date of birth must be in the past."; }
      if (!f.permanent_country) { e.permanent_country = "Country is required."; }
      if (!f.permanent_province) { e.permanent_province = "Province is required."; }
      if (!f.permanent_city) { e.permanent_city = "City is required."; }
      if (!f.document_type) { e.document_type = "Document type is required."; }
      if (!f.document_number) { e.document_number = "Document number is required."; }
      // On an update, an already-uploaded image may be kept (no re-selection needed).
      const has = {};
      this.documentImages.forEach(function (i) { has[i.label] = true; });
      if (!this.files.selfie && !has["Selfie"]) { e.selfie = "A selfie is required."; }
      if (!this.files.front_image && !has["Front Image"]) { e.front_image = "Front image is required."; }
      if (this.backRequired && !this.files.back_image && !has["Back Image"]) {
        e.back_image = "Back image is required for this document type.";
      }
      this.errors = e;
      return Object.keys(e).length === 0;
    },
    todayIso() {
      const d = new Date();
      const m = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      return d.getFullYear() + "-" + m + "-" + day;
    },
    buildFormData() {
      const f = this.form;
      const fd = new FormData();
      const same = !!f.temporary_same_as_permanent;
      const textKeys = [
        "gender", "marital_status", "occupation",
        "permanent_country", "permanent_province", "permanent_city",
        "permanent_postal_code", "permanent_street",
        "date_of_birth", "document_type", "document_number",
      ];
      textKeys.forEach(function (k) {
        fd.append(k, f[k] === null || f[k] === undefined ? "" : String(f[k]).trim());
      });
      // Ward is no longer asked for, but the API still expects the field. Send the
      // agreed default rather than an empty string, which a required validator would
      // reject just as surely as a missing key.
      fd.append("permanent_ward", DEFAULT_WARD);
      fd.append("temporary_ward", DEFAULT_WARD);

      fd.append("no_criminal_records", f.no_criminal_records ? "true" : "false");
      fd.append("temporary_same_as_permanent", same ? "true" : "false");
      // When "same as permanent" is ticked, mirror the permanent values rather than
      // sending blanks - the API stores both address blocks.
      const tempMap = {
        temporary_country: same ? f.permanent_country : f.temporary_country,
        temporary_province: same ? f.permanent_province : f.temporary_province,
        temporary_city: same ? f.permanent_city : f.temporary_city,
        temporary_postal_code: same ? f.permanent_postal_code : f.temporary_postal_code,
        temporary_street: same ? f.permanent_street : f.temporary_street,
      };
      Object.keys(tempMap).forEach(function (k) {
        fd.append(k, tempMap[k] === null || tempMap[k] === undefined ? "" : String(tempMap[k]).trim());
      });
      const files = this.files;
      ["selfie", "front_image", "back_image"].forEach(function (k) {
        if (files[k]) { fd.append(k, files[k]); }
      });
      return fd;
    },
    submit() {
      this.formError = "";
      if (!this.validate()) { return; }
      this.isSaving = true;
      const vm = this;
      const asRequest = this.isUpdateRequest;
      const payload = this.buildFormData();
      // Locked record -> /kyc/update-requests ; otherwise -> /kyc
      const call = asRequest ? this.requestUserKycUpdate(payload) : this.submitUserKyc(payload);
      return call.then(function (res) {
        if (res && res.ok) {
          vm.infoText = res.message || (asRequest
            ? "Your update request has been sent. The changes appear once an admin approves them."
            : "Your KYC has been submitted for review.");
          vm.$bvModal.hide("user-kyc-modal");
          vm.$bvToast.show("kyc-toast");
          return vm.load();
        }
        const fe = (res && res.fieldErrors) || {};
        Object.keys(fe).forEach(function (k) { vm.$set(vm.errors, k, fe[k]); });
        vm.formError = (res && res.message) ||
          (asRequest ? "Unable to send the update request." : "Unable to submit your KYC.");
      }).then(function () { vm.isSaving = false; })
        .catch(function () { vm.isSaving = false; });
    },
  },
};
</script>

<style lang="scss" scoped>
.kyc-info-card { min-height: 420px; }
.section-head { display: flex; align-items: center; justify-content: space-between; margin-bottom: 10px; }
.section-head h5 { margin: 0; font-size: 16px; font-weight: 600; }
.status-pill { font-size: 11px; padding: 2px 10px; border-radius: 10px; font-weight: 700; }
.status-pill.is-success { background: #e3f7e3; color: #00a800; }
.status-pill.is-pending { background: #fdf3e0; color: #e08b00; }
.status-pill.is-muted { background: #eef1f4; color: #6c757d; }
.tier-line { font-size: 13px; color: #55606a; margin-bottom: 12px; }
.capitalize { text-transform: capitalize; }
.limit-group { margin-bottom: 14px; }
.limit-group h6 {
  font-size: 12px; text-transform: uppercase; letter-spacing: .4px;
  color: #6c757d; font-weight: 700; margin-bottom: 6px;
  border-bottom: 1px solid #eef1f4; padding-bottom: 5px;
}
.limit-row {
  display: flex; justify-content: space-between; font-size: 13px;
  padding: 4px 0; border-bottom: 1px solid #f6f7f9;
}
.limit-row:last-child { border-bottom: none; }
.limit-row span { color: #6c757d; }
.notice {
  background: #eef1f4; border-radius: 8px; padding: 10px 12px;
  font-size: 13px; color: #55606a;
}
.notice.pending { background: #fdf3e0; color: #8a5a00; }
.notice.done { background: #e3f7e3; color: #007a00; }
.grp {
  font-size: 12px; text-transform: uppercase; letter-spacing: .4px; font-weight: 700;
  color: #6c757d; margin: 14px 0 8px; border-bottom: 1px solid #eef1f4; padding-bottom: 5px;
}
.form-row-x { margin-bottom: 12px; }
.form-row-x label { display: block; font-size: 13px; color: #6c757d; margin-bottom: 4px; }
.text-danger { color: #dc3545; font-size: 12px; display: block; margin-top: 3px; }
.muted { color: #6c757d; font-size: 12px; }
.small { font-size: 12px; }
.detail-row {
  display: flex; justify-content: space-between; padding: 8px 2px;
  border-bottom: 1px solid #f2f4f6; font-size: 14px; gap: 16px;
}
.detail-row:last-child { border-bottom: none; }
.detail-row span { color: #6c757d; }

/* Bootstrap .row negative margins overflowed the modal body; keep gutters inside. */
.kyc-form .row { margin-left: -6px; margin-right: -6px; }
.kyc-form [class*="col-"] { padding-left: 6px; padding-right: 6px; }
.kyc-form input, .kyc-form select, .kyc-form textarea { max-width: 100%; }
.kyc-form { overflow-x: hidden; }

.detail-sec { margin-bottom: 6px; }
.img-grid { display: flex; gap: 12px; flex-wrap: wrap; }
.img-cell { text-align: center; }
.img-label { font-size: 12px; color: #6c757d; margin-bottom: 4px; }
.img-cell img {
  width: 140px; height: 100px; object-fit: cover; cursor: pointer;
  border: 1px solid #eef1f4; border-radius: 6px; background: #fff;
}
.preview-img { max-width: 100%; max-height: 70vh; object-fit: contain; }

/* ---- KYC details popup ---- */
.kyc-detail-body { background: #fbfcfd; }
.kd-head {
  display: flex; gap: 16px; align-items: center;
  background: #fff; border: 1px solid #eef1f4; border-radius: 10px;
  padding: 16px 18px; margin-bottom: 16px;
}
.kd-avatar {
  width: 68px; height: 68px; border-radius: 50%; overflow: hidden; flex: 0 0 auto;
  background: #eef4fb; display: flex; align-items: center; justify-content: center;
}
.kd-avatar img { width: 100%; height: 100%; object-fit: cover; cursor: pointer; }
.kd-avatar .material-icons { font-size: 34px; color: #9fb6cc; }
.kd-head-body { min-width: 0; }
.kd-name { font-size: 17px; font-weight: 700; color: #1b1b1b; }
.kd-sub { font-size: 13px; color: #6c757d; margin-top: 1px; }
.kd-badges { display: flex; gap: 8px; margin-top: 8px; flex-wrap: wrap; }
.tier-pill {
  font-size: 11px; font-weight: 700; padding: 2px 10px; border-radius: 10px;
  background: #eef1f4; color: #55606a;
}
.kd-sec {
  background: #fff; border: 1px solid #eef1f4; border-radius: 10px;
  padding: 14px 18px; margin-bottom: 12px;
}
.kd-grp {
  font-size: 11px; text-transform: uppercase; letter-spacing: .5px; font-weight: 700;
  color: #9aa4ae; margin: 0 0 10px; padding-bottom: 8px; border-bottom: 1px solid #f2f4f6;
}
.kd-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 10px 24px; }
@media (max-width: 575px) { .kd-grid { grid-template-columns: 1fr; } }
.kd-item { display: flex; flex-direction: column; min-width: 0; }
.kd-label { font-size: 11px; color: #9aa4ae; text-transform: uppercase; letter-spacing: .3px; }
.kd-value { font-size: 14px; color: #1b1b1b; font-weight: 600; word-break: break-word; }
.kd-docs { display: flex; gap: 12px; flex-wrap: wrap; }
.kd-doc {
  width: 168px; border: 1px solid #eef1f4; border-radius: 8px; overflow: hidden;
  cursor: pointer; background: #fff; transition: border-color .15s ease;
}
.kd-doc:hover { border-color: #0066b3; }
.kd-doc img { width: 100%; height: 108px; object-fit: cover; display: block; background: #f4f6f8; }
.kd-doc-foot {
  display: flex; align-items: center; justify-content: space-between;
  padding: 6px 9px; font-size: 12px; color: #55606a; border-top: 1px solid #f2f4f6;
}
.kd-doc-foot .material-icons { font-size: 15px; color: #0066b3; }
.kd-note { font-size: 12px; color: #6c757d; margin: 4px 2px 0; }
</style>
